// Phase 31 (plan 02): media rendering (photo / video / voice / audio / document /
// sticker) for the reusable chat surface. Extracted from
// miniapp/src/components/tickets/MessageList.tsx — behavior identical, only the
// file location changed and config now comes from ChatConfigContext instead of
// module-level constants.
//
// INVARIANT: do NOT branch on message.ticketId. The Bayon embed adapter
// (plan 04, bayonMessageToChatMessage) intentionally sets ticketId='' because
// Bayon messages have no ticket scope. Only MessageList's gated divider
// (showTicketDividers={true}) is allowed to read ticketId.

import { FileText, MapPin, BarChart3, Dices, MessageSquareDashed } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { bridgeService } from '../../services/bridge';
import { TgsSticker } from './TgsSticker';
import { VoicePlayer } from './VoicePlayer';
import { useChatConfig } from './ChatConfigContext';
import {
  buildMediaProxyUrl,
  fetchSignedMediaUrlWithConfig,
  isPreSignedMediaUrl,
  useMediaBlobUrl,
} from './useMediaBlobUrl';

// Same defaulting rule as useMediaBlobUrl — used by the URL normalizer below.
const API_URL = import.meta.env.VITE_API_URL || window.location.origin;

// Strip any host baked into `url` and re-anchor it on the current API origin.
// Defends against the cached-bundle stale-hostname failure mode on local dev:
// `dev.sh` rotates Cloudflare tunnel hostnames every restart, and the SuperApp
// caches the miniapp bundle aggressively, so a build pinned to yesterday's
// tunnel URL would try to dial a dead host. Relative paths are also made
// absolute so the native bridge (which requires absolute URLs) can fetch them.
// Pre-signed URLs (matched by `isPreSignedMediaUrl` — single source of truth in
// useMediaBlobUrl.ts) are returned untouched so their signature stays valid.
function toCurrentOriginUrl(url: string): string {
  if (!url) return '';
  if (isPreSignedMediaUrl(url)) return url;
  try {
    const parsed = new URL(url);
    const apiBase = new URL(API_URL);
    return `${apiBase.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url.startsWith('/') ? `${API_URL}${url}` : `${API_URL}/${url}`;
  }
}

// Issue #118: iOS WKWebView (the embed runtime inside the SuperApp) blocks
// <video> autoplay outright — even for muted/playsInline content. An MP4
// rendered via <video> decoded correctly but never displayed a frame until
// the user tapped, because the autoplay policy is host-app-level, not a
// codec issue. The server transcodes animated WebM stickers to a sibling
// animated GIF; on iOS we render that GIF via <img>, which has no autoplay
// policy and animates immediately. Android / desktop keep using the WebM
// via <video> (autoplay there is fine, and WebM is smaller than GIF).
//
// UA never changes within a session, so memoize at module load. iPad on
// iPadOS 13+ reports a Macintosh UA — match Mac + touch capability to
// catch that path too. SSR-safe (returns false when navigator undefined).
const IS_IOS: boolean = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  if (
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  ) return true;
  return false;
})();

// Pick the right sticker asset for the runtime. Triggers the .webm → .gif
// swap on iOS only when the source is actually a video sticker
// (mediaType='video' + isTelegramSticker — webhook.ts routes Telegram's
// `is_video` flag through this combo) OR the key extension itself is
// .webm (defensive: catches any path where mediaType isn't 'video' but
// the file is). Non-iOS, non-sticker, non-webm callers pass through
// unchanged.
function stickerKeyForRuntime(
  mediaKey: string | undefined,
  mediaType: string | undefined,
  isTelegramSticker: boolean,
): string | undefined {
  if (!mediaKey || !IS_IOS) return mediaKey;
  const isVideoSticker = mediaType === 'video' && isTelegramSticker;
  const looksLikeWebm = mediaKey.toLowerCase().endsWith('.webm');
  if (!isVideoSticker && !looksLikeWebm) return mediaKey;
  return mediaKey.replace(/\.webm$/i, '.gif');
}

// Placeholder strings that stand in for media — never shown as bubble text
export const MEDIA_PLACEHOLDERS = new Set([
  '[Media message]', '[voice]', '[document]', '[photo]', '[video]', '[audio]', '[sticker]',
]);

// Non-downloadable inbound message types (location / poll / dice, plus the
// generic catch-all) are stored by the server as a bracket token — there is no
// file to render. Instead of leaking the raw "[token]" as bubble text, the chat
// shows a compact attachment-style card that matches the document-card chrome.
const NON_MEDIA_LABELS: Record<
  string,
  { icon: LucideIcon; title: string; subtitle: string; accent: string }
> = {
  '[location]': { icon: MapPin, title: 'Location', subtitle: 'Shared a location', accent: '#2F80ED' },
  '[poll]': { icon: BarChart3, title: 'Poll', subtitle: 'Sent a poll', accent: '#7C5CFC' },
  '[dice]': { icon: Dices, title: 'Dice', subtitle: 'Sent a dice', accent: '#27AE60' },
  '[message]': { icon: MessageSquareDashed, title: 'Unsupported message', subtitle: "Can't be shown here", accent: '#7A818C' },
};

// Token set for membership checks from other components (mirrors the
// MEDIA_PLACEHOLDERS pattern — a const export, so it stays Fast-Refresh-safe).
export const NON_MEDIA_LABEL_TOKENS = new Set(Object.keys(NON_MEDIA_LABELS));

/** Attachment-style card for non-downloadable message types (location/poll/…). */
export function NonMediaLabel({ text }: { text: string }) {
  const def = NON_MEDIA_LABELS[text];
  if (!def) return null;
  const Icon = def.icon;
  return (
    <div
      className="flex items-center gap-2.5 rounded-[16px] px-3 py-2.5 w-full min-w-[200px]"
      style={{ background: 'rgba(241,243,246,0.82)' }}
    >
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
        style={{ background: def.accent }}
      >
        <Icon size={18} className="text-white" strokeWidth={2.2} aria-hidden />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-[#16181D] leading-tight">{def.title}</div>
        <div className="text-[11px] text-[#7A818C] mt-1 leading-tight">{def.subtitle}</div>
      </div>
    </div>
  );
}

// Media types that render as a standalone bare element — no white chat-bubble
// chrome behind them. Photos already did; videos now match because Telegram
// delivers GIFs as mp4 (mediaType='video') and the bubble background made the
// "GIF" look pasted on a card instead of integrated like an inline image.
// Regular videos benefit too: same chat-app convention (Telegram, WhatsApp,
// Signal) treats inline videos as standalone media, not bubble content.
export const STANDALONE_MEDIA_TYPES = new Set(['photo', 'voice', 'audio', 'document', 'video']);

// File extensions we render inline as images (via the same path as native
// Telegram "photo" messages) even when mediaType='document'. This covers the
// case where a user sends a .jpg as a file attachment instead of a compressed
// photo, so it lands server-side as mediaType='document' but we still want
// the chat bubble to show the actual image instead of a generic file card.
// Deliberately excluded: svg (XSS risk from embedded scripts), ico (favicon —
// weird at full size), tiff (inconsistent WebView decoders), RAW camera
// formats (dng/cr2/nef/arw — no browser decoder).
const IMAGE_EXTS = new Set([
  'jpg', 'jpeg', 'jfif',
  'png', 'apng',
  'gif',
  'webp',
  'bmp',
  'heic', 'heif',
  'avif',
]);

function extensionOf(fileName?: string, url?: string): string | undefined {
  const src = fileName?.includes('.') ? fileName : url;
  if (!src) return undefined;
  // Strip query string / fragment before extracting the extension so signed
  // URLs (…/foo.jpg?x-…) don't yield "jpg?x-algorithm=..." as the ext.
  const clean = src.split(/[?#]/, 1)[0];
  const dot = clean.lastIndexOf('.');
  if (dot < 0) return undefined;
  return clean.slice(dot + 1).toLowerCase();
}

function isImageByExtension(fileName?: string, url?: string): boolean {
  const ext = extensionOf(fileName, url);
  return !!ext && IMAGE_EXTS.has(ext);
}

function formatFileSize(fileSize?: number): string | null {
  if (!Number.isFinite(fileSize) || !fileSize || fileSize <= 0) return null;

  if (fileSize < 1024) return `${fileSize} B`;

  const units = ['KB', 'MB', 'GB'];
  let value = fileSize / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const precision = value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(precision).replace(/\.0+$|(?<=\.[0-9])0$/, '')} ${units[unitIndex]}`;
}

function mediaExtFromUrlOrName(fileName?: string, url?: string): string {
  const fromName = fileName?.includes('.') ? fileName.split('.').pop()?.toLowerCase() : '';
  if (fromName && fromName.length <= 8) return fromName;
  if (!url) return '';
  try {
    const path = new URL(url, 'https://placeholder.local').pathname;
    const seg = path.split('/').pop() ?? '';
    const ext = seg.includes('.') ? seg.split('.').pop()?.toLowerCase().split('?')[0] : '';
    return ext && ext.length <= 8 ? ext : '';
  } catch {
    const seg = url.split('/').pop() ?? '';
    return seg.includes('.') ? (seg.split('.').pop()?.toLowerCase().split('?')[0] ?? '') : '';
  }
}

function getFileExtensionLabel(fileName?: string, url?: string): string {
  const candidate = fileName || url;
  const ext = candidate?.split('.').pop()?.trim().toUpperCase();
  return ext && ext.length <= 5 ? ext : 'FILE';
}

function getDocumentAccent(extension: string): string {
  if (extension === 'PDF') return '#FF5A3D';
  if (extension === 'DOC' || extension === 'DOCX') return '#2563EB';
  if (extension === 'XLS' || extension === 'XLSX' || extension === 'CSV') return '#16A34A';
  if (extension === 'PPT' || extension === 'PPTX') return '#F97316';
  return '#4B5563';
}

// Small skeleton shown while a blob URL is being fetched
function MediaLoadingPlaceholder() {
  return (
    <div
      className="rounded-xl mb-1 animate-pulse"
      style={{ width: 200, height: 120, background: 'rgba(0,0,0,0.08)' }}
    />
  );
}

export interface MediaContentProps {
  mediaType?: string;
  // Issue #119 Phase 3: prop renamed r2Key → mediaKey. Same value
  // (`messages.media_key` / `messages.r2_key` — dual-write keeps them
  // identical), more honest name.
  mediaKey?: string;
  fallbackUrl: string;
  fileName?: string;
  fileSize?: number;
  isFromUser: boolean;
  presentation?: 'bubble' | 'bare';
  /** Telegram video sticker: short silent loop, no scrubber chrome */
  isTelegramSticker?: boolean;
  /** Authoritative whole-second duration for voice/audio media, from the
   *  server (Telegram's payload or the miniapp recorder). Lets the player
   *  show the length without decoding the file. */
  durationSeconds?: number;
  /** Message sequence (ascending with time) for voice auto-advance chaining. */
  seq?: number;
}

export function MediaContent({
  mediaType,
  mediaKey,
  fallbackUrl,
  fileName,
  fileSize,
  isFromUser,
  presentation = 'bubble',
  isTelegramSticker = false,
  durationSeconds,
  seq,
}: MediaContentProps) {
  const config = useChatConfig();
  // Issue #118: on iOS, swap the .webm sticker key for the .gif sibling
  // produced by the server-side transcode. Returns the original key on
  // non-iOS or for non-sticker media so all other render paths are
  // unaffected. Computed before useMediaBlobUrl so the proxy fetches the
  // iOS-compatible asset directly — no second round-trip.
  const effectiveMediaKey = stickerKeyForRuntime(mediaKey, mediaType, isTelegramSticker);
  // Prefer the pre-signed URL (Bayon flow returns /api/media/dl/<HMAC-token>
  // in `fallbackUrl`). The hook uses it as the render src directly — no
  // proxy fetch, no auth header. Ticket flow leaves fallbackUrl empty/undefined
  // and falls through to the auth-gated proxy fetch path.
  const blobUrl = useMediaBlobUrl(effectiveMediaKey, fallbackUrl || undefined);


  // `isPreSignedMediaUrl` is imported from useMediaBlobUrl.ts — single
  // source of truth for the pre-signed-URL pattern. Saves a 401 round-trip
  // on the Bayon side where the sign route doesn't exist; ticket flow's
  // `fallbackUrl` is empty/different, so the shortcut doesn't fire there.

  // Inline mode — image/video opens in the in-app browser viewer, no
  // filesystem save. Closure over `config` so two simultaneous embeds with
  // different ChatConfig values don't clobber each other.
  const openMedia = async (key: string | undefined, url: string) => {
    const raw = isPreSignedMediaUrl(url)
      ? url
      : (key && (await fetchSignedMediaUrlWithConfig(config, key, { attachment: false }))) || url;
    if (!raw) return;
    const target = toCurrentOriginUrl(raw);
    bridgeService.openExternalLink(target, 'inAppBrowserView').catch(() => {
      window.open(target, '_blank');
    });
  };

  const openDocument = async (key: string | undefined, url: string, name?: string) => {
    const ext = name?.includes('.')
      ? name.split('.').pop()!.toLowerCase()
      : url.split('.').pop()?.toLowerCase() ?? '';

    const imageExts = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic']);
    const docExts = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv']);
    const fileType = imageExts.has(ext) ? 'image' : docExts.has(ext) ? 'doc' : 'anyFile';
    const safeFileName = name || (ext ? `file.${ext}` : 'file');

    // Text-previewable types open in the in-app browser instead of routing
    // through shareBase64 → native share sheet. The server now returns
    // text/plain for these (see storageService.ts content-type map), so iOS
    // Safari renders the content inline as scrollable readable text. PDF is
    // included because Safari renders application/pdf inline natively, so
    // there's no reason to go through the share sheet for it either.
    // If the signed-URL fetch fails for any reason, fall through to the
    // existing shareBase64 path below — the rollback path is automatic.
    // Types we want to open in the in-app browser instead of the native
    // share sheet. Two paths inside this branch:
    //   - htmlPreviewExts → server-side HTML preview endpoint (/api/media/
    //     preview/<token>). Returns text/html so iOS can't use the filename
    //     extension to override the type. Best for tabular/text formats.
    //   - inlineRawExts   → raw bytes through /api/media/dl/<token> with
    //     Content-Disposition: inline; filename=…; iOS renders inline for
    //     types it recognizes (PDF in particular).
    const htmlPreviewExts = new Set(['csv', 'txt', 'md', 'log', 'json', 'xml']);
    const inlineRawExts = new Set(['pdf']);
    if (htmlPreviewExts.has(ext) || inlineRawExts.has(ext)) {
      try {
        const raw = isPreSignedMediaUrl(url)
          ? url
          : (key && (await fetchSignedMediaUrlWithConfig(config, key, { attachment: false, fileName: safeFileName }))) || null;
        if (raw) {
          // For text/CSV/JSON/etc., swap /dl/ → /preview/ so the server
          // wraps the bytes in HTML (table for CSV, <pre> for text). For
          // PDF and friends iOS can render the raw bytes inline, so leave
          // the URL pointed at /dl/.
          const finalRaw = htmlPreviewExts.has(ext)
            ? raw.replace('/api/media/dl/', '/api/media/preview/')
            : raw;
          const target = toCurrentOriginUrl(finalRaw);
          await bridgeService.openExternalLink(target, 'inAppBrowserView');
          return;
        }
      } catch (err) {
        console.warn('inline preview open failed, falling back to share path:', err);
      }
    }

    // Bayon flow shortcut: when the caller already has a pre-signed URL
    // (mediaUrl: /api/media/dl/<HMAC-token>), pull the bytes from there
    // directly — no Authorization header, no proxy route. The HMAC token in
    // the URL IS the auth. Saves the 401 hop that would otherwise happen
    // because the auth-gated proxy route doesn't exist for Bayon's prefix.
    if (isPreSignedMediaUrl(url)) {
      try {
        const res = await fetch(url);
        if (res.ok) {
          const buf = await res.arrayBuffer();
          const ok = await bridgeService.shareBase64(buf, safeFileName, fileType);
          if (ok) return;
        }
      } catch (err) {
        console.warn('pre-signed shareBase64 failed, falling back to URL share:', err);
      }
    }

    // Preferred path: pull bytes via the auth-gated proxy and hand them to the
    // bridge with the exact original filename. Bridge writes the buffer to a
    // temp file using `safeFileName` verbatim, so native viewers (Files app,
    // PDF reader, etc.) open it under "Resume.pdf" with extension intact.
    //
    // Why not URL-share: bridge.shareFile -> downloadAndShare downloads the URL
    // and derives the saved filename from the URL path segment. Our short
    // signed-URL shape (/api/media/dl/<token>) means the saved file lands as
    // "<token>" with no extension — OS shows "Unknown file" and won't open it.
    if (key) {
      try {
        const token = localStorage.getItem(config.authTokenKey);
        if (token) {
          const res = await fetch(buildMediaProxyUrl(config.mediaProxyBase, key), {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const buf = await res.arrayBuffer();
            const ok = await bridgeService.shareBase64(buf, safeFileName, fileType);
            if (ok) return;
          }
        }
      } catch (err) {
        console.warn('shareBase64 path failed, falling back to URL share:', err);
      }
    }

    // Fallback: URL-based share. Used when not running inside the SuperApp
    // (browser dev) or if the binary-share path fails. Server sets
    // Content-Disposition so the in-app browser still saves with a sane name.
    const raw = (key && (await fetchSignedMediaUrlWithConfig(config, key, { fileName: safeFileName }))) || url;
    if (!raw) return;
    const target = toCurrentOriginUrl(raw);

    // `shareFile` resolves with `true`/`false` (it never rejects), so we check
    // the return value instead of relying on `.catch()`. The native iOS share
    // sheet has a known bug in the SuperApp bridge where the share sheet
    // rejects with `sharePositionOrigin: argument must be set` — falling back
    // to the in-app browser viewer routes around that entirely (Safari renders
    // PDFs/images inline and exposes the system share menu without the broken
    // anchor).
    const shared = await bridgeService.shareFile(target, fileType, ext, safeFileName);
    if (shared) return;

    const opened = await bridgeService
      .openExternalLink(target, 'inAppBrowserView')
      .then(() => true)
      .catch(() => false);
    if (!opened) {
      window.open(target, '_blank');
    }
  };

  // Upgrade image-typed documents to render via the photo path. Telegram
  // delivers files sent via "Send as file" as mediaType='document' even when
  // they are images, and the miniapp upload flow also falls back to 'document'
  // for anything outside the narrow image/* MIME check (e.g. image/webp was
  // explicitly excluded). Fall back to extension sniffing so both paths end
  // up inline.
  const effectiveMediaType =
    mediaType === 'document' && isImageByExtension(fileName, fallbackUrl)
      ? 'photo'
      : mediaType;

  const ext = mediaExtFromUrlOrName(fileName, fallbackUrl);

  switch (effectiveMediaType) {
    case 'photo':
      if (!blobUrl) return <MediaLoadingPlaceholder />;
      return (
        <img
          src={blobUrl}
          alt="Photo"
          className={presentation === 'bare'
            ? 'block rounded-[18px] max-w-full cursor-pointer shadow-sm'
            : 'rounded-xl max-w-full mb-1 cursor-pointer'}
          onClick={() => { void openMedia(mediaKey, fallbackUrl); }}
        />
      );

    case 'video':
      if (!blobUrl) return <MediaLoadingPlaceholder />;
      if (isTelegramSticker) {
        // Issue #118: on iOS render the sibling animated GIF via <img>
        // instead of the WebM via <video>. The SuperApp's WKWebView blocks
        // <video> autoplay outright, but <img> elements have no autoplay
        // policy and animate immediately. effectiveR2Key has already been
        // swapped to .gif for iOS by stickerKeyForRuntime; the blob URL
        // therefore wraps GIF bytes when on iOS, WebM bytes elsewhere.
        return (
          <div className={presentation === 'bare' ? 'mb-0' : 'mb-1'}>
            {IS_IOS ? (
              <img
                src={blobUrl}
                alt="Animated sticker"
                className={
                  presentation === 'bare'
                    ? 'block max-h-[200px] max-w-[200px] rounded-[18px] shadow-sm'
                    : 'mb-1 max-h-[200px] max-w-[200px] rounded-xl'
                }
              />
            ) : (
              <video
                src={blobUrl}
                className={
                  presentation === 'bare'
                    ? 'block max-h-[200px] max-w-[200px] rounded-[18px] shadow-sm'
                    : 'mb-1 max-h-[200px] max-w-[200px] rounded-xl'
                }
                playsInline
                loop
                muted
                autoPlay
              />
            )}
          </div>
        );
      }
      return (
        <div className="mb-1">
          <video src={blobUrl} controls className="rounded-xl max-w-full" playsInline />
        </div>
      );

    case 'voice':
    case 'audio':
      if (!blobUrl) return <MediaLoadingPlaceholder />;
      return <VoicePlayer audioUrl={blobUrl} presentation={presentation} isFromUser={isFromUser} durationSeconds={durationSeconds} seq={seq} />;

    case 'document':
    default:
      {
        // Animated / static stickers are delivered as "document" by Telegram
        // (tgs = gzipped Lottie, webm = video sticker, webp = static sticker).
        // Render these inline without the generic download-button chrome.
        if (ext === 'webm' || ext === 'gif') {
          // Issue #118: same iOS handling as the primary 'video' branch
          // above. On iOS the upstream key was swapped .webm → .gif by
          // stickerKeyForRuntime, so ext is 'gif' here and the <img>
          // path renders. On other platforms ext is 'webm' and <video>
          // plays.
          if (!blobUrl) return <MediaLoadingPlaceholder />;
          return (
            <div className={presentation === 'bare' ? 'mb-0' : 'mb-1'}>
              {ext === 'gif' ? (
                <img
                  src={blobUrl}
                  alt="Animated sticker"
                  className={
                    presentation === 'bare'
                      ? 'block max-h-[200px] max-w-[200px] rounded-[18px] shadow-sm'
                      : 'mb-1 max-h-[200px] max-w-[200px] rounded-xl'
                  }
                />
              ) : (
                <video
                  src={blobUrl}
                  className={
                    presentation === 'bare'
                      ? 'block max-h-[200px] max-w-[200px] rounded-[18px] shadow-sm'
                      : 'mb-1 max-h-[200px] max-w-[200px] rounded-xl'
                  }
                  playsInline
                  loop
                  muted
                  autoPlay
                />
              )}
            </div>
          );
        }

        if (ext === 'webp') {
          if (!blobUrl) return <MediaLoadingPlaceholder />;
          return (
            <img
              src={blobUrl}
              alt="Sticker"
              className={
                presentation === 'bare'
                  ? 'block max-h-[200px] max-w-[200px] cursor-pointer rounded-[18px] object-contain shadow-sm'
                  : 'mb-1 max-h-[200px] max-w-[200px] cursor-pointer rounded-xl object-contain'
              }
              onClick={() => { void openMedia(mediaKey, fallbackUrl); }}
            />
          );
        }

        if (ext === 'tgs') {
          if (!blobUrl) return <MediaLoadingPlaceholder />;
          return (
            <div className={presentation === 'bare' ? '' : 'mb-1'}>
              <TgsSticker url={blobUrl} />
            </div>
          );
        }

        const extensionLabel = getFileExtensionLabel(fileName, fallbackUrl);
        const documentAccent = getDocumentAccent(extensionLabel);
        const formattedSize = formatFileSize(fileSize);

        return (
          <button
            type="button"
            onClick={() => { void openDocument(mediaKey, fallbackUrl, fileName); }}
            className={presentation === 'bare'
              ? 'flex items-center gap-3 rounded-[20px] px-3.5 py-3 w-full min-w-[240px] text-left transition shadow-sm'
              : 'flex items-center gap-2.5 rounded-[16px] px-3 py-2.5 mb-1 w-full text-left transition'}
            style={presentation === 'bare'
              ? { background: '#F1F3F6', border: '1px solid rgba(15,23,42,0.06)' }
              : { background: 'rgba(241,243,246,0.82)' }}
          >
            <span
              className={presentation === 'bare'
                ? 'flex h-11 w-11 shrink-0 items-center justify-center rounded-full'
                : 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full'}
              style={{ background: documentAccent }}
            >
              <FileText size={18} className="text-white" strokeWidth={2.2} aria-hidden />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#16181D] leading-tight break-words">
                {fileName || 'Download file'}
              </div>
              {formattedSize && (
                <div className="text-[11px] text-[#7A818C] mt-1 leading-tight">
                  {formattedSize}
                </div>
              )}
            </div>
            <div className="shrink-0 pl-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#7A818C]">
              {extensionLabel}
            </div>
          </button>
        );
      }
  }
}
